import React, { useEffect, useLayoutEffect, useRef } from 'react';
import { View, Modal, TouchableOpacity, ScrollView, Animated } from 'react-native';
import { translate } from '../services/translate';
import MyAppText from './MyAppText';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from './CustomButton';
import { WindowInformation } from '../services/window-information';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import FontWeight from '../styles/fontWeights';

const modalWidth = {
    'small': 450,
    'medium': 650,
    'large': 850,
    'extra-large': 1010,
};

type ModalWidth = keyof typeof modalWidth;
type ModalHeight = 'fit' | 'fill';
type ModalType = 'destructive' | 'warning';

type ModalProps = {
    isModalVisible: boolean;
    setModalVisible: (value: boolean) => void;
    width: ModalWidth;
    /**
     * default value 'fill'
     */
    mobileHeight?: ModalHeight;
    animateMobileHeight?: boolean;
    title: string;
    titleIcon?: IconDefinition;
    children: React.ReactNode;
    /**
     * defines color of the primary button and
     * title icon
     */
    type?: 'destructive' | 'warning';
    disableScrollView?: boolean;
    primaryButton?: {
        text: string;
        /**
         * you can return `false` if you want the modal to keep open
         */
        onClick: (() => void | false) | (() => Promise<void | false>);
        disabled?: boolean;
    };
    secondaryButton?: {
        text: string;
        onClick: (() => void) | (() => Promise<void>);
        disabled?: boolean;
    };
    cancelButton?: {
        customText?: string;
        onClick?: (() => void) | (() => Promise<void>);
    };
} & ({
    titleIcon: IconDefinition;
    type: ModalType;
} | {
    titleIcon?: never;
    type?: ModalType;
});

export default function BasicModal({
    isModalVisible,
    setModalVisible,
    width = 'medium',
    mobileHeight = 'fill',
    animateMobileHeight = false,
    title,
    titleIcon,
    children,
    type,
    primaryButton,
    cancelButton,
    disableScrollView,
    secondaryButton,
}: ModalProps) {
    const { styles, theme } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const translateY = useRef(new Animated.Value(1000)).current;
    const container = useRef<View>(null);
    const previousHeight = useRef<number>();
    const previousMobileHeight = useRef(mobileHeight);
    const heightAnimation = useRef<Animation>();

    useLayoutEffect(() => {
        if (!animateMobileHeight || !windowInfo.isMobile) {
            return;
        }
        const element = container.current as unknown as HTMLElement | null;
        if (!element) {
            return;
        }
        const nextHeight = element.getBoundingClientRect().height;
        if (previousHeight.current && previousMobileHeight.current !== mobileHeight
            && typeof element.animate === 'function' && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
            heightAnimation.current?.cancel();
            heightAnimation.current = element.animate([
                { height: `${previousHeight.current}px` },
                { height: `${nextHeight}px` },
            ], { duration: 240, easing: 'cubic-bezier(0.2, 0, 0, 1)' });
        }
        previousHeight.current = nextHeight;
        previousMobileHeight.current = mobileHeight;
    }, [animateMobileHeight, windowInfo.isMobile, mobileHeight]);

    useEffect(() => () => heightAnimation.current?.cancel(), []);

    useEffect(() => {
        if (!windowInfo.isMobile) {
            translateY.setValue(0);
            return;
        }

        Animated.spring(translateY, {
            toValue: isModalVisible ? 0 : 1000,
            useNativeDriver: true,
        }).start();
    }, [isModalVisible, windowInfo.isMobile]);

    return (
        <Modal
            visible={isModalVisible}
            animationType={'fade'}
            onRequestClose={async () => {
                if (cancelButton?.onClick) {
                    await cancelButton.onClick();
                }
                setModalVisible(false);
            }}
            transparent={true}
        >
            <View style={styles.shadow}>
                <Animated.View
                    style={{
                        flex: 1,
                        transform: [{ translateY }],
                    }}
                >
                    <View style={styles.background(windowInfo.isMobile)}>
                        <View ref={container} style={styles.container(windowInfo.isMobile, modalWidth[width], mobileHeight)}
                            onLayout={({ nativeEvent: { layout } }) => {
                                if (animateMobileHeight && windowInfo.isMobile) {
                                    previousHeight.current = layout.height;
                                }
                            }}>
                            <View style={styles.titleContainer}>
                                {titleIcon && <FontAwesomeIcon
                                    icon={titleIcon}
                                    color={type === 'destructive' ? theme.colors.errors[300] : theme.colors.warnings[400]}
                                    width={24}
                                    fontSize={19}
                                    style={{ paddingTop: 2 }}
                                />}
                                <MyAppText style={styles.titleText}>{title}</MyAppText>

                                {windowInfo.isMobile &&
                                    <TouchableOpacity
                                        style={{ paddingLeft: 24 }}
                                        onPress={async () => {
                                            if (cancelButton?.onClick) {
                                                await cancelButton.onClick();
                                            }
                                            setModalVisible(false);
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faXmark}
                                            color={theme.colors.neutral[400]}
                                            fontSize={23}
                                            width={24}
                                        />
                                    </TouchableOpacity>
                                }
                            </View>

                            {!disableScrollView ?
                                <ScrollView
                                    horizontal={false}
                                    contentContainerStyle={{ gap: 8, flex: 1, zIndex: 3 }}
                                >
                                    {children}
                                </ScrollView>
                                : <View style={{ flex: 1, gap: 8, zIndex: 3 }}>
                                    {children}
                                </View>
                            }

                            <View style={{
                                flexDirection: windowInfo.isMobile ? 'column-reverse' : 'row',
                                width: '100%',
                                justifyContent: windowInfo.isMobile ? undefined : 'space-between',
                                gap: 8,
                            }}>

                                {(secondaryButton || !windowInfo.isMobile) &&
                                    <View>
                                        {secondaryButton &&
                                            <CustomButton
                                                textStyle={styles.buttonText}
                                                onPress={secondaryButton.onClick}
                                                isLoading={false}
                                                type={'tertiary'}
                                                text={secondaryButton.text} />
                                        }
                                    </View>
                                }


                                <View style={{ flexDirection: windowInfo.isMobile ? 'column-reverse' : 'row', gap: 8 }}>
                                    {!windowInfo.isMobile &&
                                        <CustomButton
                                            textStyle={styles.buttonText}
                                            onPress={async () => {
                                                if (cancelButton?.onClick) {
                                                    await cancelButton.onClick();
                                                }
                                                setModalVisible(false);
                                            }}
                                            text={cancelButton?.customText ?? translate('cancel')}
                                            type='tertiary' />
                                    }
                                    {primaryButton &&
                                        <CustomButton
                                            textStyle={styles.buttonText}
                                            onPress={async () => {
                                                const shouldClose = await primaryButton.onClick();
                                                if (shouldClose !== false) {
                                                    setModalVisible(false);
                                                }
                                            }}
                                            type={type}
                                            disabled={primaryButton.disabled}
                                            text={primaryButton.text} />
                                    }
                                </View>
                            </View>
                        </View>
                    </View>
                </Animated.View>
            </View>
        </Modal>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    shadow: {
        backgroundColor: '#31313199',
        flex: 1,
    },
    background: (isMobile: boolean) => ({
        flexDirection: 'column',
        justifyContent: isMobile ? 'flex-end' : 'center',
        alignItems: 'center',
        paddingTop: isMobile ? 60 : 0,
        flex: 1
    }),
    container: (isMobile: boolean, maxWidth: number, mobileHeight: ModalHeight) => ({
        gap: 24,
        width: '100%',
        maxWidth: !isMobile ? maxWidth : undefined,
        height: isMobile ? (mobileHeight === 'fill' ? '100%' : undefined) : undefined,
        maxHeight: isMobile ? '100%' : '85%',
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
        borderBottomLeftRadius: !isMobile ? 12 : 0,
        borderBottomRightRadius: !isMobile ? 12 : 0,
        padding: 24,
        backgroundColor: theme.colors.container.background,
    }),
    titleContainer: {
        flexDirection: 'row',
        minHeight: 25,
        gap: 8,
    },
    buttonText: {
        fontSize: 16,
    },
    titleText: {
        fontSize: 18,
        fontWeight: FontWeight.bold,
        color: theme.colors.neutral[500],
        flex: 1,
    },
}));
